import {
	AlertDialog,
	Button,
	DialogContainer,
	Heading,
	Tab,
	TabList,
	TabPanel,
	Tabs,
} from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useCallback, useMemo, useState } from 'react';
import type { Key } from 'react-aria-components';

import { useBridgeApi } from '@/api/BridgeApi';
import { useUserId } from '@/auth/UserIdContext';
import { useBackToChat } from '@/hooks/useBackToChat';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useOptimisticState } from '@/hooks/useOptimisticState';
import { SpaceLayout } from '@/layouts/SpaceLayout';
import { cn } from '@/utils/cn';
import { trackEvent } from '@/utils/fullstory';

import type { Product } from '../../../../bridge/types/business-profile.ts';

import { BrandLoadingInterstitial } from './BrandLoadingInterstitial';
import { AdditionalLearningsTab } from './tabs/AdditionalLearningsTab';
import { BusinessSummaryTab } from './tabs/BusinessSummaryTab';
import { CompetitorsTab } from './tabs/CompetitorsTab';
import { ProductsTab } from './tabs/ProductsTab';
import { VisualIdentityTab } from './tabs/VisualIdentityTab';
import { VoiceTab } from './tabs/VoiceTab';
import { useBusinessProfileApi } from './useBusinessProfileApi';
import { useCompetitorsApi } from './useCompetitorsApi';

// Tab id is the stable key for TabPanel/activeTab; it matches the display label for every
// tab today. The two stay separate fields so a label can be reworded without silently
// changing the key that TabPanel, the panel-height branch, and the switch analytics use.
type BrandTab = { id: string; label: string };

const BASE_TABS: BrandTab[] = [
	{ id: 'Business Summary', label: 'Business Summary' },
	{ id: 'Visual Identity', label: 'Visual Identity' },
	{ id: 'Voice', label: 'Voice' },
	{ id: 'Similar Businesses', label: 'Similar Businesses' },
	{ id: 'Products', label: 'Products' },
];

const ADDITIONAL_LEARNINGS_TAB: BrandTab = {
	id: 'Additional Learnings',
	label: 'Additional Learnings',
};

export function BrandSpace() {
	const userId = useUserId();
	const api = useBusinessProfileApi(userId);
	const competitorsApi = useCompetitorsApi(userId);
	const profile = api.profile;
	const bridgeApi = useBridgeApi();
	const handleBack = useBackToChat();
	const businessProfileFailed =
		bridgeApi.user_state?.onboarding_status?.business_profile_failed === true;
	const businessProfileReady =
		bridgeApi.user_state?.onboarding_status?.business_profile_ready === true;
	const businessProfileReviewed =
		bridgeApi.user_state?.onboarding_status?.business_profile_reviewed === true;
	const [activeTab, setActiveTab] = useState<Key>('Business Summary');
	const [newCompetitorId, setNewCompetitorId] = useState<string | null>(null);
	// Strategy generation is what fills this record today, so it rides the same flag.
	const additionalLearningsEnabled = useFeatureFlag('strategies');
	const tabs = additionalLearningsEnabled
		? [...BASE_TABS, ADDITIONAL_LEARNINGS_TAB]
		: BASE_TABS;

	// One-time nudge shown the first time a user lands on their freshly generated
	// profile, before it has been reviewed. Purely informational: the reviewed
	// flag is flipped implicitly when the user leaves the Brand space (see
	// NavBar). Local dismissal keeps it out of the way for the rest of the visit.
	const [reviewNoticeDismissed, setReviewNoticeDismissed] = useState(false);
	const showReviewNotice =
		businessProfileReady && !businessProfileReviewed && !reviewNoticeDismissed;

	const reviewNotice = (
		<DialogContainer onDismiss={() => setReviewNoticeDismissed(true)}>
			{showReviewNotice && (
				<AlertDialog
					variant="information"
					title="Review your business profile"
					primaryActionLabel="Got it"
					autoFocusButton="primary"
					onPrimaryAction={() => setReviewNoticeDismissed(true)}
				>
					Bridgeway uses your business profile to generate your content. Please
					review these details carefully — you can edit anything that
					doesn&apos;t look right.
				</AlertDialog>
			)}
		</DialogContainer>
	);

	const serverProducts = useMemo(
		() => profile?.products?.product ?? [],
		[profile?.products?.product],
	);
	const [displayProducts, setOptimisticProducts] = useOptimisticState<
		Product[] | null
	>(serverProducts);
	const [newProductIndex, setNewProductIndex] = useState<number | null>(null);

	const handleAddProduct = useCallback(() => {
		trackEvent('Profile Product Added');
		const current = displayProducts ?? serverProducts;
		const emptyProduct: Product = {
			name: { value: null, inferred: false },
			description: { value: null, inferred: false },
			benefits: { value: null, inferred: false },
			features: { value: null, inferred: false },
			images: { value: null, inferred: false },
			price: { value: null, inferred: false },
		};
		setOptimisticProducts([...current, emptyProduct]);
		setNewProductIndex(current.length);
		void api.onAddProduct();
	}, [api, displayProducts, serverProducts, setOptimisticProducts]);

	const handleRemoveProduct = useCallback(
		(index: number) => {
			trackEvent('Profile Product Removed', { index });
			const current = displayProducts ?? serverProducts;
			setOptimisticProducts(current.filter((_, i) => i !== index));
			void api.onRemoveProduct(index);
		},
		[api, displayProducts, serverProducts, setOptimisticProducts],
	);

	const handleOptimisticProductUpdate = useCallback(
		(products: Product[]) => {
			setOptimisticProducts(products);
		},
		[setOptimisticProducts],
	);

	const handleClearNewProduct = useCallback(() => {
		setNewProductIndex(null);
	}, []);

	const handleClearNewCompetitor = useCallback(() => {
		setNewCompetitorId(null);
	}, []);

	// Stable identity so CompetitorsTab's memo isn't defeated by a fresh closure
	// on every BrandSpace render. refetch is itself memoized, so depending on it
	// alone (not the whole competitorsApi object, which is new every render) keeps
	// this callback stable across renders.
	const handleCompetitorRetry = useCallback(
		() => void competitorsApi.refetch(),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[competitorsApi.refetch],
	);

	const businessName =
		profile?.my_business?.business_name?.value ?? 'My Business';

	if (!profile && businessProfileFailed) {
		return (
			<SpaceLayout title="Brand" onBack={handleBack}>
				<div
					className="flex h-full flex-col items-center justify-center gap-2 p-8
						text-center"
				>
					<p className="text-base font-semibold">
						We couldn&apos;t build your business profile.
					</p>
					<p className="text-sm text-gray-500">
						Something went wrong on our side. Try reloading in a few minutes.
					</p>
				</div>
			</SpaceLayout>
		);
	}

	// A returning user reaches Brand only after onboarding is done, so a null
	// profile here means the load failed, not that onboarding is incomplete. Must
	// sit after the businessProfileFailed branch (which owns the build-failure
	// copy) and before the spinner branch, whose `!profile` clause would otherwise
	// swallow this and spin forever.
	if (!profile && api.error && !api.loading) {
		return (
			<SpaceLayout title="Brand" onBack={handleBack}>
				<div
					className="flex h-full flex-col items-center justify-center gap-3 p-8
						text-center"
				>
					<p className="text-base font-semibold">
						We couldn&apos;t load your business profile.
					</p>
					<p className="text-sm text-gray-500">
						Something went wrong on our side. Please try again.
					</p>
					<Button variant="accent" size="S" onPress={() => void api.refetch()}>
						Try again
					</Button>
				</div>
			</SpaceLayout>
		);
	}

	if (api.loading || !profile) {
		return (
			<SpaceLayout title="Brand" onBack={handleBack}>
				<BrandLoadingInterstitial />
			</SpaceLayout>
		);
	}

	return (
		<SpaceLayout title="Brand" onBack={handleBack} fillHeight>
			{reviewNotice}
			<Tabs
				selectedKey={activeTab}
				onSelectionChange={(key) => {
					trackEvent('Profile Tab Switched', { tab: String(key) });
					setActiveTab(key);
				}}
				aria-label="Brand"
				UNSAFE_className="flex min-h-0 flex-1 flex-col overflow-hidden"
			>
				<div className="flex-shrink-0 pb-3">
					<div className="flex flex-col gap-1 pb-6">
						<span className="text-sm text-gray-500">Brand</span>
						<Heading
							level={1}
							styles={style({
								font: 'heading',
								fontWeight: 'bold',
								fontSize: 'heading-lg',
							})}
						>
							{businessName}
						</Heading>
					</div>
					<TabList>
						{tabs.map((tab) => (
							<Tab key={tab.id} id={tab.id}>
								{tab.label}
							</Tab>
						))}
					</TabList>
				</div>
				<div
					className={cn(
						'scrollbar-hidden min-h-0 flex-1',
						// The learnings doc owns its own scroll (only the editable content
						// scrolls), so its panel must be height-bounded, not page-scrolled.
						activeTab === ADDITIONAL_LEARNINGS_TAB.id
							? 'flex flex-col overflow-hidden pb-8'
							: 'overflow-y-auto pb-8',
					)}
				>
					<TabPanel id="Business Summary">
						<div className="mt-6">
							<BusinessSummaryTab
								profile={profile}
								onSave={api.onSave}
								onArraySave={api.onArraySave}
								onNumberSave={api.onNumberSave}
								onIcpAgeSave={api.onIcpAgeSave}
								onPositioningSave={api.onPositioningSave}
							/>
						</div>
					</TabPanel>
					<TabPanel id="Visual Identity">
						<div className="mt-6">
							<VisualIdentityTab
								profile={profile}
								onSave={api.onSave}
								onArraySave={api.onArraySave}
								onImageUrlsSave={api.onImageUrlsSave}
							/>
						</div>
					</TabPanel>
					<TabPanel id="Voice">
						<div className="mt-6">
							<VoiceTab profile={profile} onArraySave={api.onArraySave} />
						</div>
					</TabPanel>
					<TabPanel id="Similar Businesses">
						<div className="mt-6">
							<CompetitorsTab
								competitors={competitorsApi.competitors}
								analyzingIds={competitorsApi.analyzingIds}
								isResearching={competitorsApi.isResearching}
								newCompetitorId={newCompetitorId}
								onUpdateCompetitor={competitorsApi.onUpdateCompetitor}
								onUpdateCompetitorArrayField={
									competitorsApi.onUpdateCompetitorArrayField
								}
								onUpdateCompetitorSocialUrl={
									competitorsApi.onUpdateCompetitorSocialUrl
								}
								onUpdateCompetitorProducts={
									competitorsApi.onUpdateCompetitorProducts
								}
								onRemoveCompetitor={competitorsApi.onRemoveCompetitor}
								onRunInsights={competitorsApi.onRunInsights}
								onResearchCompetitor={competitorsApi.onResearchCompetitor}
								researchingIds={competitorsApi.researchingIds}
								researchedIds={competitorsApi.researchedIds}
								failedResearchIds={competitorsApi.failedResearchIds}
								onClearNewCompetitor={handleClearNewCompetitor}
								error={competitorsApi.error}
								onRetry={handleCompetitorRetry}
							/>
						</div>
					</TabPanel>
					<TabPanel id="Products">
						<div className="mt-6">
							<ProductsTab
								profile={profile}
								optimisticProducts={displayProducts}
								newProductIndex={newProductIndex}
								onSave={api.onSave}
								onArraySave={api.onArraySave}
								onAddProduct={handleAddProduct}
								onRemoveProduct={handleRemoveProduct}
								onOptimisticUpdate={handleOptimisticProductUpdate}
								onClearNewProduct={handleClearNewProduct}
							/>
						</div>
					</TabPanel>
					{additionalLearningsEnabled && (
						// shouldForceMount: this panel holds a document the owner edits and saves
						// explicitly. Unmounting it on a tab switch would throw away typing they
						// hadn't saved yet, with no warning and no way back.
						<TabPanel id="Additional Learnings" shouldForceMount>
							{/* h-full rather than flex-1: S2's own tabPanel style sets
							    display:block and wins over UNSAFE_className, so the panel
							    can't be made a flex container — but flexGrow:1 does give it
							    a definite height to size against. */}
							<div className="flex h-full min-h-0 flex-col pt-6">
								<AdditionalLearningsTab />
							</div>
						</TabPanel>
					)}
				</div>
			</Tabs>
		</SpaceLayout>
	);
}
